<template>
  <v-card class="px-6 py-4">
    <div class="d-flex justify-space-between align-center">
      <v-skeleton-loader v-if="isLoading" type="text" width="80" />

      <div v-else>
        <div class="d-flex align-center">
          <span
            data-testid="teamInfo-text-name"
            class="text-subtitle-2 font-weight-bold"
            v-snip="5"
            >{{ team.name }}</span
          >
          <v-tooltip v-if="!team.site" top>
            <template v-slot:activator="{ on, attrs }">
              <v-hover v-slot="{ hover }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  class="ms-2"
                  size="20"
                  :color="hover ? 'primary' : 'blue-grey lighten-1'"
                  >mdi-web</v-icon
                >
              </v-hover>
            </template>

            {{ $t('teams.global_team') }}
          </v-tooltip>
        </div>
      </div>

      <TeamOptions
        class="mr-n2"
        :team="team"
        @deleted="handleDelete"
        @updated="handleUpdate"
      />
    </div>

    <p
      v-if="team.description"
      class="my-3 text-subtitle-2 blue-grey--text text--darken-1"
    >
      {{ team.description }}
    </p>

    <div class="d-flex flex-column mt-4">
      <div v-if="team.site" class="d-flex align-center">
        <p class="caption blue-grey--text text--darken-1 mb-0 me-2">
          {{ $t('routines.created_in') }}
        </p>

        <v-chip
          small
          color="blue-grey lighten-3"
          class="caption blue-grey--text text--darken-3 cursor-default"
          outlined
        >
          {{ siteName }}
        </v-chip>
      </div>
    </div>

    <TeamMembersSkeleton v-if="isLoading" />

    <TeamMembers
      v-else
      :permissions="permissions"
      :team="team"
      @updated="$emit('updated')"
    />
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'
import teamPermissions from '@/mixins/teamPermissions.vue'

import TeamOptions from '@/components/TeamOptions/TeamOptions.vue'
import TeamMembers from './TeamMembers.vue'
import TeamMembersSkeleton from './TeamMembersSkeleton.vue'

export default {
  name: 'TeamInfo',

  mixins: [trackerMixin, mixin, teamPermissions],

  components: {
    TeamOptions,
    TeamMembers,
    TeamMembersSkeleton,
  },

  props: {
    team: {
      type: Object,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      prevRoute: null,
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.prevRoute = from
    })
  },

  computed: {
    ...mapGetters(['organizationSites']),

    permissions() {
      return this.getTeamPermissions({ team: this.team })
    },

    siteName() {
      return this.team.site
        ? this.organizationSites?.find?.(site => site.id === this.team?.site)
            ?.name ?? ''
        : null
    },
  },

  methods: {
    handleDelete() {
      this.trackTeamDelete({ view: 'detail' })
      if (this.prevRoute?.name) {
        this.$router.back()
      } else {
        this.navigateTo({
          name: 'teams',
        })
      }
    },

    handleUpdate(e) {
      this.trackTeamEdit({ view: 'detail' })
      this.$emit('updated', e)
    },
  },
}
</script>

<style lang="scss" scoped>
.people-title-container {
  border-bottom: 1px solid $color-blue-grey-lighten2;
}
</style>
